import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Phone } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { FORM_ENDPOINT, FORM_SUCCESS_REDIRECTS, isSpamSubmission } from "@/lib/forms";

interface CallbackFormProps {
  onClose?: () => void;
}

const CallbackForm = ({ onClose }: CallbackFormProps) => {
  const [formData, setFormData] = useState({
    nom: "",
    tel: "",
    email: "",
    ville: "",
    service: "",
    message: "",
    horaire: "",
  });
  const [date, setDate] = useState<Date>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const { toast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Removed URL parameter detection - now using fetch mode

  // Nettoyage du timeout au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const services = [
    "Couverture",
    "Zinguerie", 
    "Peinture Hydrofuge",
    "Nettoyage",
    "Démoussage",
    "Remplacement",
    "Rénovation",
    "Tuile/Ardoise",
    "Velux",
    "Charpente",
    "Isolation",
    "Autre"
  ];

  const horaires = [
    "8h-10h",
    "10h-12h",
    "12h-14h",
    "14h-16h",
    "16h-18h",
    "18h-20h"
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation anti-spam honeypot
    if (isSpamSubmission(honeypot)) {
      toast({
        title: "Erreur",
        description: "Soumission invalide détectée.",
        variant: "destructive"
      });
      return;
    }

    // Validation des champs obligatoires
    if (!formData.nom.trim() || !formData.tel.trim() || !formData.email.trim() || !formData.service) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    // Validation des longueurs
    if (formData.nom.trim().length > 100 || formData.tel.trim().length > 20 || 
        formData.email.trim().length > 255 || formData.message.trim().length > 1000) {
      toast({
        title: "Erreur",
        description: "Certains champs dépassent la longueur maximale autorisée",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Timeout de sécurité
    timeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: "Problème de connexion",
        description: "La requête prend trop de temps. Veuillez réessayer.",
        variant: "destructive",
      });
    }, 12000);
    
    try {
      const submitData = new FormData();
      submitData.append('_subject', 'Demande de rappel - Jacky Couverture');
      submitData.append('nom', formData.nom);
      submitData.append('telephone', formData.tel);
      submitData.append('email', formData.email);
      submitData.append('ville', formData.ville);
      submitData.append('service', formData.service);
      submitData.append('date_souhaitee', date ? format(date, "dd/MM/yyyy", { locale: fr }) : '');
      submitData.append('horaire_rappel', formData.horaire);
      submitData.append('message', formData.message);
      submitData.append('website', honeypot);

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        body: submitData,
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (response.ok) {
        toast({
          title: "Demande envoyée",
          description: "Nous vous rappelons rapidement.",
        });
        setFormData({
          nom: "",
          tel: "",
          email: "",
          ville: "",
          service: "",
          message: "",
          horaire: "",
        });
        setDate(undefined);
        onClose?.();
      } else {
        throw new Error('Erreur serveur');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Erreur d'envoi",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-primary text-center">
        Demande de rappel gratuit
      </h2>

      <form 
        onSubmit={handleSubmit} 
        className="space-y-4"
      >
        {/* Honeypot anti-spam (invisible) */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="nom">Nom Prénom *</Label>
            <Input
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={(e) => handleInputChange("nom", e.target.value)}
              required
              placeholder="Votre nom et prénom"
            />
          </div>
          
          <div>
            <Label htmlFor="tel">Téléphone *</Label>
            <Input
              id="tel"
              name="telephone"
              type="tel"
              value={formData.tel}
              onChange={(e) => handleInputChange("tel", e.target.value)}
              required
              placeholder="06.xx.xx.xx.xx"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
              placeholder="votre@email.fr"
            />
          </div>
          
          <div>
            <Label htmlFor="ville">Ville *</Label>
            <Input
              id="ville"
              name="ville"
              value={formData.ville}
              onChange={(e) => handleInputChange("ville", e.target.value)}
              required
              placeholder="Votre ville"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="service">Service *</Label>
          <input type="hidden" name="service" value={formData.service} />
          <Select value={formData.service} onValueChange={(value) => handleInputChange("service", value)} required>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionnez un service" />
            </SelectTrigger>
            <SelectContent>
              {services.map((service) => (
                <SelectItem key={service} value={service}>
                  {service}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Date souhaitée</Label>
            <input type="hidden" name="date_souhaitee" value={date ? format(date, "dd/MM/yyyy", { locale: fr }) : ""} />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  type="button"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "dd/MM/yyyy", { locale: fr }) : "Choisir une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label htmlFor="horaire">Horaire de rappel</Label>
            <input type="hidden" name="horaire_rappel" value={formData.horaire} />
            <Select value={formData.horaire} onValueChange={(value) => handleInputChange("horaire", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un horaire" />
              </SelectTrigger>
              <SelectContent>
                {horaires.map((horaire) => (
                  <SelectItem key={horaire} value={horaire}>
                    {horaire}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={(e) => handleInputChange("message", e.target.value)}
            placeholder="Décrivez votre projet ou vos besoins..."
            rows={3}
          />
        </div>

        <Button 
          type="submit" 
          className="w-full gradient-primary text-white hover:opacity-90 transition-smooth py-6 text-lg"
          disabled={isSubmitting}
        >
          <Phone className="mr-2 h-5 w-5" />
          {isSubmitting ? "Envoi en cours..." : "Demander un rappel"}
        </Button>
      </form>
    </div>
  );
};

export default CallbackForm;